import { useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useMemo } from 'react';

import { ContentType } from '../domain';
import {
  selectContentComposerContentRepurposingState,
  selectContentComposerEmailAudienceConfig,
} from '../selector';

export interface ContentTagPillsProps {
  contentType: ContentType;
  toneOfVoice: string;
  topicId: string;
  recipientTypeId?: string;
  emailTypeId?: string;
  className?: string;
}

export const ContentTagPills: React.FC<ContentTagPillsProps> = ({
  contentType,
  toneOfVoice,
  topicId,
  recipientTypeId,
  emailTypeId,
  className = '',
}) => {
  const { t } = useTranslation();

  // Get content repurposing state to resolve topic ID to label
  const contentRepurposingState = useAppSelector(
    selectContentComposerContentRepurposingState,
  );

  // Get email audience config to resolve recipient type and email type IDs to labels
  const emailAudienceConfig = useAppSelector(
    selectContentComposerEmailAudienceConfig,
  );

  // Get tone of voice label from translation
  const getToneLabel = (toneId: string): string => {
    return t(
      `content-recording.content-composer.tone-of-voice.options.${toneId}`,
      { defaultValue: toneId },
    );
  };

  // Resolve topic ID to label from content repurposing state
  const topicLabel = useMemo(() => {
    if (
      contentRepurposingState.status === 'success' &&
      contentRepurposingState.data &&
      contentRepurposingState.data.eligibility === 'ELIGIBLE' &&
      contentRepurposingState.data.topics.value
    ) {
      const topic = contentRepurposingState.data.topics.value.find(
        t => t.id === topicId,
      );
      return topic?.topic ?? topicId;
    }
    return topicId;
  }, [contentRepurposingState, topicId]);

  // Resolve recipient type ID to translated label
  const recipientTypeLabel = useMemo(() => {
    if (
      !recipientTypeId ||
      emailAudienceConfig.status !== 'success' ||
      !emailAudienceConfig.data
    ) {
      return recipientTypeId ?? '';
    }
    const audience = emailAudienceConfig.data.find(
      a => a.key === recipientTypeId,
    );
    return audience ? t(audience.labelKey) : recipientTypeId;
  }, [recipientTypeId, emailAudienceConfig, t]);

  // Resolve email type ID to translated label
  const emailTypeLabel = useMemo(() => {
    if (
      !recipientTypeId ||
      !emailTypeId ||
      emailAudienceConfig.status !== 'success' ||
      !emailAudienceConfig.data
    ) {
      return emailTypeId ?? '';
    }
    const audience = emailAudienceConfig.data.find(
      a => a.key === recipientTypeId,
    );
    if (!audience) {
      return emailTypeId;
    }
    const emailType = audience.types.find(t => t.key === emailTypeId);
    return emailType ? t(emailType.labelKey) : emailTypeId;
  }, [recipientTypeId, emailTypeId, emailAudienceConfig, t]);

  // Build pills array - tone is always shown
  const pills: Array<{ label: string; testId: string }> = [
    {
      label: getToneLabel(toneOfVoice),
      testId: 'tag-pill-tone',
    },
  ];

  // Add topic pill for non-EMAIL content types
  if (contentType !== 'EMAIL') {
    pills.push({
      label: topicLabel,
      testId: 'tag-pill-topic',
    });
  }

  // Add recipient type and email type for Email content type
  if (contentType === 'EMAIL') {
    if (recipientTypeLabel) {
      pills.push({
        label: recipientTypeLabel,
        testId: 'tag-pill-recipient-type',
      });
    }
    if (emailTypeLabel) {
      pills.push({
        label: emailTypeLabel,
        testId: 'tag-pill-email-type',
      });
    }
  }

  return (
    <div
      className={cx(
        'flex flex-wrap items-start gap-2 w-full h-auto pb-3',
        'border-b border-white/10',
        className,
      )}
      data-testid="content-tag-pills"
    >
      {pills.map((pill, index) => (
        <span
          key={index}
          className={cx(
            'px-3 py-1 rounded-full',
            'bg-transparent border border-spring-neutral-b4',
            'text-xs font-medium leading-[1.5] text-spring-neutral-b4-muted',
            'min-h-6',
          )}
          data-testid={pill.testId}
        >
          {pill.label}
        </span>
      ))}
    </div>
  );
};
export const ContentTagPillsPlaceholder = () => {
  return (
    <div
      className={cx(
        'flex flex-wrap items-start gap-2 w-full h-auto pb-3',
        'border-b border-white/10',
      )}
      data-testid="content-tag-pills-placeholder"
    >
      <span
        key={'content-tag-pill-placeholder-1'}
        className={cx(
          'bg-spring-neutral-b4 border border-spring-neutral-b4',
          'h-6 w-24 animate-pulse rounded-full',
        )}
        data-testid={'content-tag-pill-placeholder-1'}
      ></span>
      <span
        key={'content-tag-pill-placeholder-2'}
        className={cx(
          'bg-spring-neutral-b4 border border-spring-neutral-b4',
          'h-6 w-24 animate-pulse rounded-full',
        )}
        data-testid={'content-tag-pill-placeholder-2'}
      ></span>
    </div>
  );
};
